import { FC, memo, useCallback, useState } from 'react'

import { useForm } from '@conform-to/react'
import { parseWithZod } from '@conform-to/zod'
import { match } from 'ts-pattern'

import { Drawer } from '~/components'
import {
  SearchResultOrder,
  useFacilitiesForm,
  useKeywordForm,
  useMonthlyCostsForm,
  useNumberOfUsersForm,
  useOfficeTypeForm,
} from '~/features/common'
import { Facility, Office, Type } from '~/models/types'

import {
  DrawerSearchForm,
  drawerSearchFormSchema,
  DrawerSearchFormType,
  FieldType,
  DrawerSearchFormValues,
} from './drawer-seach-form'
import { DrawerSearchContentDetail } from './drawer-search-content-detail'
import { DrawerSearchContentList } from './drawer-search-content-list'
import {
  drawerContent,
  useMapContext,
  useMapDispatchContext,
} from '../../context'

const useWatchFormValues = (fields: FieldType) => {
  const officeTypes = Array.isArray(fields.officeTypes.value)
    ? fields.officeTypes.value.filter((val): val is string => val !== undefined)
    : []
  const facilities = Array.isArray(fields.facilities.value)
    ? fields.facilities.value.filter((val): val is string => val !== undefined)
    : []
  const minPeople = fields.minPeople.value || ''
  const minMonthlyRent = fields.minMonthlyRent.value || ''
  const maxMonthlyRent = fields.maxMonthlyRent.value || ''
  const keyword = fields.keyword.value || ''

  const [watchedFormValues, setWatchedFormValues] =
    useState<DrawerSearchFormValues>({
      officeTypes,
      facilities,
      minPeople,
      minMonthlyRent,
      maxMonthlyRent,
      keyword,
    })

  const changeWatchedFormValues = (formData: DrawerSearchFormValues) => {
    setWatchedFormValues((prevState) => ({
      ...prevState,
      ...formData,
    }))
  }

  const changeWatchedOfficeTypes = (officeTypes: string[]) => {
    changeWatchedFormValues?.({ officeTypes: officeTypes })
  }
  const changeWatchedMinPeople = (minPeople: string) => {
    changeWatchedFormValues?.({ minPeople: minPeople })
  }
  const changeWatchedMinMonthlyRent = (minMonthlyRent: string) => {
    changeWatchedFormValues?.({ minMonthlyRent: minMonthlyRent })
  }
  const changeWatchedMaxMonthlyRent = (maxMonthlyRent: string) => {
    changeWatchedFormValues?.({ maxMonthlyRent: maxMonthlyRent })
  }
  const changeWatchedFacilities = (facilities: string[]) => {
    changeWatchedFormValues?.({ facilities: facilities })
  }
  const changeWatchedKeyword = (keyword: string) => {
    changeWatchedFormValues?.({ keyword: keyword })
  }

  return {
    watchedFormValues,
    changeWatchedFormValues,
    changeWatchedOfficeTypes,
    changeWatchedMinPeople,
    changeWatchedMinMonthlyRent,
    changeWatchedMaxMonthlyRent,
    changeWatchedFacilities,
    changeWatchedKeyword,
  }
}

const useDrawerSearchForm = (
  initialFormValues: DrawerSearchFormValues,
  officeTypes: Type[],
  facilities: Pick<Facility, 'id' | 'name' | 'order'>[],
  mapCenterLocation: React.ComponentProps<
    typeof DrawerSearchForm
  >['mapCenterLocation'],
) => {
  const { setDrawerContent } = useMapDispatchContext()
  const [form, fields] = useForm<DrawerSearchFormType>({
    id: 'searchForm',
    shouldValidate: 'onBlur',
    shouldRevalidate: 'onInput',
    onValidate({ formData }) {
      return parseWithZod(formData, { schema: drawerSearchFormSchema })
    },
    onSubmit() {
      setDrawerContent(drawerContent.list)
    },
    defaultValue: initialFormValues,
  })

  // FIXME: fields.areas.valueではフォームの値の変更を検知の挙動が安定しないため、変更検知用の別にフォームの現在の値を保持するstateを作成した。冗長なので将来的にはconformが提供する値に置き換えたい。
  const {
    watchedFormValues,
    changeWatchedFacilities,
    changeWatchedKeyword,
    changeWatchedOfficeTypes,
    changeWatchedMinPeople,
    changeWatchedMinMonthlyRent,
    changeWatchedMaxMonthlyRent,
  } = useWatchFormValues(fields)
  const {
    value: officeTypesValue,
    onChange: onChangeOfficeTypes,
    name: officeTypeInputName,
  } = useOfficeTypeForm(fields.officeTypes, changeWatchedOfficeTypes)
  const {
    value: minPeopleValue,
    onChange: onChangeMinPeople,
    name: minPeopleInputName,
  } = useNumberOfUsersForm(fields.minPeople, changeWatchedMinPeople)
  const {
    minMonthlyRentValue,
    maxMonthlyRentValue,
    onChangeMinMonthlyRent,
    onChangeMaxMonthlyRent,
    minMonthlyRentName,
    maxMonthlyRentName,
    onBlurMinMonthlyRent,
    onBlurMaxMonthlyRent,
  } = useMonthlyCostsForm(
    fields.minMonthlyRent,
    fields.maxMonthlyRent,
    changeWatchedMinMonthlyRent,
    changeWatchedMaxMonthlyRent,
  )
  const {
    value: facilitiesValue,
    onChange: onChangeFacilities,
    name: facilitiesInputName,
  } = useFacilitiesForm(fields.facilities, changeWatchedFacilities)
  const {
    value: keywordValue,
    onChange: onChangeKeyword,
    name: keywordInputName,
    onEnterKeyDown: onEnterKeyword,
    onBlur: onBlurKeyword,
  } = useKeywordForm(fields.keyword, changeWatchedKeyword)

  const officeTypeFormOptions = officeTypes.map(({ id, name }) => ({
    label: name,
    value: id.toString(),
  }))

  const facilitiesAndServicesFormOptions = facilities
    .sort((a, b) => (a.order ?? Infinity) - (b.order ?? Infinity))
    .map(({ id, name }) => ({
      label: name,
      facilityId: id.toString(),
    }))

  return {
    form,
    fields,
    officeTypeFormOptions,
    facilitiesAndServicesFormOptions,
    mapCenterLocation,
    watchedFormValues,
    officeTypesValue,
    onChangeOfficeTypes,
    officeTypeInputName,
    minPeopleValue,
    onChangeMinPeople,
    minPeopleInputName,
    minMonthlyRentValue,
    maxMonthlyRentValue,
    onChangeMinMonthlyRent,
    onChangeMaxMonthlyRent,
    minMonthlyRentName,
    maxMonthlyRentName,
    onBlurMinMonthlyRent,
    onBlurMaxMonthlyRent,
    facilitiesValue,
    onChangeFacilities,
    facilitiesInputName,
    keywordValue,
    onChangeKeyword,
    keywordInputName,
    onEnterKeyword,
    onBlurKeyword,
  }
}

export type DrawerSearchContentProps = {
  offices: Office[]
  searchResultCount: number
  sortOrder: SearchResultOrder
  changeSortOrder: (sortOrder: SearchResultOrder) => void
} & React.ComponentProps<typeof DrawerSearchForm>

const DrawerSearchContent = (props: DrawerSearchContentProps) => {
  const { currentDrawerContent } = useMapContext()

  const {
    changeSortOrder,
    sortOrder,
    offices,
    searchResultCount,
    ...formProps
  } = props

  return match(currentDrawerContent)
    .with(drawerContent.detail, () => <DrawerSearchContentDetail />)
    .with(drawerContent.searchForm, () => <DrawerSearchForm {...formProps} />)
    .otherwise(() => (
      <DrawerSearchContentList
        changeSortOrder={changeSortOrder}
        offices={offices}
        searchResultCount={searchResultCount}
        sortOrder={sortOrder}
      />
    ))
}

type DrawerProps = {
  position?: React.ComponentProps<typeof Drawer>['position']
  className?: string
  initialFormValues: DrawerSearchFormValues
  offices: Office[]
  searchResultCount: number
  sortOrder: SearchResultOrder
  changeSortOrder: (sortOrder: SearchResultOrder) => void
  mapCenterLocation: React.ComponentProps<
    typeof DrawerSearchForm
  >['mapCenterLocation']
  officeTypes: Type[]
  facilities: Pick<Facility, 'id' | 'name' | 'order'>[]
}

const DrawerSearchResultComponent: FC<DrawerProps> = ({
  position = 'bottom',
  className,
  initialFormValues,
  changeSortOrder,
  offices,
  searchResultCount,
  sortOrder,
  mapCenterLocation,
  officeTypes,
  facilities,
}) => {
  const { isOpenDrawer } = useMapContext()
  const { openDrawer, closeDrawer } = useMapDispatchContext()

  const toggleDrawer = useCallback(() => {
    isOpenDrawer ? closeDrawer() : openDrawer()
  }, [isOpenDrawer, closeDrawer, openDrawer])

  const formContext = useDrawerSearchForm(
    initialFormValues,
    officeTypes,
    facilities,
    mapCenterLocation,
  )

  return (
    <Drawer
      className={className}
      isOpen={isOpenDrawer}
      position={position}
      toggleIsOpen={toggleDrawer}
    >
      <DrawerSearchContent
        changeSortOrder={changeSortOrder}
        offices={offices}
        searchResultCount={searchResultCount}
        sortOrder={sortOrder}
        {...formContext}
      />
    </Drawer>
  )
}

export const DrawerSearchResult = memo(DrawerSearchResultComponent)
